import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDebounceValue } from 'usehooks-ts';
import { IoMdAdd } from 'react-icons/io';
import Modal from '~/lib/Modal';
import { useGetEtaiements, useGetEtaiementsList } from '~/hooks/etaiements';
import styles from './etaiements.module.scss';
import FilterSearchParam from '~/components/FilterSearchParam';
import InputSearch from '~/components/InputSearch';
import CreateEtaiement from '~/components/etaiement/form/CreateEtaiement';
import EtaiementsTable from '~/components/table/EtaiementsTable';
import Pagination from '~/components/Pagination';

const Etaiements = () => {
  const modalRef = useRef<any>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { search = '', ...paramsObject } = Object.fromEntries(searchParams);
  const [debouncedSearch] = useDebounceValue(search, 500);
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const hasPagination = params.has('limit') || params.has('page') || params.has('skip');
    if (!hasPagination) {
      params.set('page', '1');
      params.set('limit', '20');
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const { data: etaiementsData } = useGetEtaiements({
    ...paramsObject,
    search: debouncedSearch,
    limit: paramsObject.limit || '20',
    page: paramsObject.page || '1',
  });

  const etaiementsList = etaiementsData?.etaiements || [];

  const { data: list = {} } = useGetEtaiementsList();
  const {
    agencies = [],
    status = [],
  } = list;

  const handleSearch = (searchValue: string) => {
    const params = new URLSearchParams(searchParams);
    params.delete('skip');
    params.delete('page');
    if (searchValue.length < 3) {
      params.delete('search');
    } else {
      params.set('search', searchValue);
    }
    setSearchParams(params);
  };

  const handleSorting = (sort: any) => {
    if (!sort) return;
    const params = new URLSearchParams(searchParams);
    params.delete('skip');
    params.delete('page');
    params.set('sortKey', sort.id.replace('_', '.'));
    params.set('order', sort.desc ? 'desc' : 'asc');
    setSearchParams(new URLSearchParams(params));
  };

  const etaiements = useMemo(() => etaiementsList, [etaiementsList]);

  return (
    <>
      <Modal width={450} ref={modalRef}>
        <CreateEtaiement onSuccess={(etaiementId: string) => {
          modalRef.current.close();
          navigate(`/etaiements/${etaiementId}`);
        }} />
      </Modal>
      <div className={styles.etaiements}>
        <div className={styles.filters}>
          <div className={styles.selects}>
            <div className={styles.field}>
              <FilterSearchParam
                label='Agence'
                name='agency'
                options={agencies}
                feminine
              />
            </div>
            <div className={styles.field}>
              <FilterSearchParam
                label='Statut'
                name='status'
                options={status}
              />
            </div>
          </div>
          <div className={styles.search}>
            <InputSearch
              defaultValue={search}
              handleSearch={handleSearch}
              label='Rechercher'
            />
          </div>
          <button onClick={() => modalRef.current.open()}>
            <span className='icon'>{IoMdAdd({})}</span>
            <span>Créer un devis étaiement</span>
          </button>
        </div>
        <div className={styles.table}>
          {(etaiements || [])?.length > 0 && (
            <EtaiementsTable
              etaiements={etaiements}
              handleSorting={handleSorting}
            />
          )}
        </div>
        <div className={styles.pagination}>
          {etaiementsData?.meta?.total && etaiementsData?.meta?.limit
          && etaiementsData.meta.total > etaiementsData.meta.limit
          && etaiementsData?.meta?.page
          && <Pagination
            currentPage={etaiementsData.meta.page}
            totalItems={etaiementsData.meta.total}
            itemsPerPage={etaiementsData.meta.limit}
          />
          }
        </div>
      </div>
    </>
  );
};

export default Etaiements;
